/* eslint-disable no-bitwise */

import {
  Component,
  OnInit,
  Renderer2,
  ElementRef,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit,
  EventEmitter,
  Output,
} from '@angular/core';
import { Store, Select, Actions, ofActionDispatched, ofActionCompleted } from '@ngxs/store';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { MarkerModel } from 'src/app/story/models/marker.model';

import { filter, takeUntil, map, distinctUntilChanged } from 'rxjs/operators';
import { EngineState } from 'src/app/engine/state/engine.state';
import { EngineService } from 'src/app/engine/engine.service';
import { EnginePositionModel } from 'src/app/engine/models/engine-position.model';
import { Observable, Subject, Subscription } from 'rxjs';

import * as Hammer from 'hammerjs';
import { HitModel } from 'src/app/engine/models/hit.model';
import { CameraService } from 'src/app/engine/camera.service';
import { LabelPinType } from 'src/app/story/models/labels/label-pin-type.enum';
import { LabelSequentialType } from 'src/app/story/models/labels/label-sequential-type.enum';
import { LabelThemeEnum } from 'src/app/story/models/labels/label-theme.enum';
import { ErrandType } from 'src/app/story/models/errands/errand-type.enum';
import { isEmpty, isEqual } from 'lodash';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { LabelsService } from 'src/app/story/labels.service';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { ColorUtils } from 'src/app/shared/libraries/colors/colors';
import { ContextLabelId } from 'src/app/story/state/labels/story.labels.state.model';
import {
  TakeChapterPicture,
  TakeChapterPictureSuccess,
} from 'src/app/story/state/thumbnails/story.thumbnails.actions';
import { HumanInterface } from 'src/app/engine/models/human.interface';

declare const Human: HumanInterface;

const fullOpacity = 1;
const fadeStartAngle = 80;
const maxViewAngle = 100;
const fadeRange = maxViewAngle - fadeStartAngle;

// Hardcoded reference number. The actual pin width is set in label.component.scss.
const pinDiameter = 32;
const pinRadius = pinDiameter / 2;

interface DisplayUpdated {
  id: string;
  inView: boolean;
  shown: boolean;
  objectShown: boolean;
}

interface PositionUpdated {
  id: string;
  canvasPos: { x: number; y: number };
  occluded: boolean;
}

interface LabelEventData {
  labelId: string;
}

@Component({
  selector: 'app-generic-label',
  templateUrl: './generic-label.component.html',
  styleUrls: ['./generic-label.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericLabelComponent implements OnInit, AfterViewInit, OnDestroy {
  labelPinType = LabelPinType;
  labelSequentialType = LabelSequentialType;

  @ViewChild('labelContainerEl', { static: true }) labelContainerRef: ElementRef;
  @ViewChild('labelEl', { static: true }) labelRef: ElementRef;
  @ViewChild('wire', { static: true }) wireRef: ElementRef;
  @ViewChild('tag', { static: true }) tagRef: ElementRef;
  @ViewChild('pin', { static: true }) pinRef: ElementRef;
  @ViewChild('title', { static: true }) titleRef: ElementRef;

  @Select(EngineState.getEnginePosition)
  enginePosition$: Observable<EnginePositionModel>;

  @Select(StoryChaptersState.getSelectedChapterBackground)
  background$: Observable<BackgroundModel>;

  @Output()
  labelUpdated = new EventEmitter<{ label: LabelModel; shouldPreserve: boolean }>();

  @Output()
  offsetUpdated = new EventEmitter<{ labelId: string; offset: [number, number] }>();

  @Output()
  pinClicked = new EventEmitter();

  @Output()
  tagClicked = new EventEmitter();

  @Output()
  displayUpdated = new EventEmitter<DisplayUpdated>();

  @Output()
  positionUpdated = new EventEmitter();

  @Output()
  hoverStart = new EventEmitter<LabelEventData>();

  @Output()
  hoverEnd = new EventEmitter<LabelEventData>();

  @Output()
  labelOffsetUpdated = new EventEmitter<LabelModel>();

  @Input()
  fit: 'fixed' | 'auto' = 'fixed';

  @Input()
  opaque = false;

  @Input()
  fixedScale = 0;

  @Input()
  sequence: number;

  @Input()
  hasContent: boolean;

  @Input()
  public get label(): LabelModel {
    return this._label;
  }
  public set label(value: LabelModel) {
    const isValueNull = isEmpty(value);
    const isLabelNull = isEmpty(this.label);

    if (isValueNull && !isLabelNull) {
      this._label = { ...value };
      this.destroyLabel();
    } else if (!isValueNull) {
      this._label = { ...value };
      this.initLabel(this.label);
    }
  }
  private _label: LabelModel;

  @Input()
  isCollapsed = false;

  @Input()
  descriptionCollapsed: boolean;

  @Input()
  tagCollapsed = false;

  @Input()
  set selected(value: boolean) {
    if (value) {
      this.tagRef.nativeElement.focus();
    }
    this._selected = value;
  }

  get selected() {
    return this._selected;
  }

  @Input()
  theme = 'classic';

  @Input()
  pinDraggable = true;

  @Input()
  tagDraggable = true;

  private _selected = false;

  // Cache position for performance
  enginePosition: EnginePositionModel = null;

  marker: MarkerModel;

  // Label visibility
  depth: Number;
  shown: boolean;
  objectShown: boolean;

  // Some labels change styles on dark background
  isBackgroundDark = false;

  // Pin
  pinDragging = false;

  // Wire
  hasWire: boolean;

  dragLabelStartEvent: HammerInput;
  cachedTagOffset: [number, number];

  // Screenshot mode (disables some effects)
  screenshotMode = false;

  // Reference to hammer event managers so we can unbind on destroy
  hammerManagers: HammerManager[] = [];

  object$: Subscription;
  displayUpdated$ = new Subject<DisplayUpdated>();
  positionUpdated$ = new Subject<PositionUpdated>();

  private ngUnsubscribe = new Subject();

  constructor(
    private renderer: Renderer2,
    private labelsService: LabelsService,
    private engineService: EngineService,
    private cameraService: CameraService,
    private store: Store,
    private actions: Actions,
    private changeDetection: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initListeners();
  }

  ngAfterViewInit() {
    // We're using HammerJS to handle drag events for better touch compatibility. However
    // this results in Hammer 'tap' events preceeding Angular 'click' events, which can
    // lead to duplicate or unexpected click handling. So for labels, all interaction events are
    // now handled through HammerJS, and initialized below.
    this.bindTagEvents();
    this.bindPinEvents();
    this.bindHoverEvents();
  }

  ngOnDestroy() {
    this.destroyLabel();

    this.emitDisplayUpdated({
      id: this.label.id,
      inView: false,
      shown: false,
      objectShown: this.objectShown,
    });

    // Unsubscribe
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  destroyLabel() {
    // Destroy event handlers bound outside of angular
    this.hammerManagers.forEach((manager) => manager.destroy);
    this.hammerManagers = [];

    // Delete markers in the engine
    this.deleteMarker(this.marker);
  }

  initListeners() {
    // Cache local copy of engine position any time it updates, for performance
    this.enginePosition$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((position) => {
      this.enginePosition = position;
    });

    // An exception of allowing another component to subscribe directly to engine events.
    // Since marker position changes with any motion from the engine, running events through
    // the store would create an unneccessary performance overhead.
    this.engineService
      .getEvent(EventsEnum.SceneMarkersUpdated)
      .pipe(
        filter(() => {
          return this.label != null && this.enginePosition != null;
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(() => {
        this.setCanvasPos();
      });

    // notify display updates
    this.displayUpdated$
      .pipe(
        // detect change
        distinctUntilChanged((last, current) => isEqual(last, current)),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((event) => {
        this.displayUpdated.next(event);
      });

    // notify position updates
    this.positionUpdated$
      .pipe(
        // detect change
        distinctUntilChanged((last, current) => isEqual(last, current)),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((positionUpdate: PositionUpdated) => {
        this.positionUpdated.next({ positionUpdate, onCanvas: this.isOnCanvas() });
      });

    // Observe background color, ensure appropriate color contrast is applied
    this.background$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((background) => {
      const outerRGB = background.colors.slice(0, 3) as [number, number, number];
      const innerRGB = background.colors.slice(4, 7) as [number, number, number];
      this.isBackgroundDark = !ColorUtils.isLight(outerRGB, innerRGB);
      this.changeDetection.markForCheck();
    });

    // Listen for screenshotting, update labels to be screenshot compatible
    this.actions
      .pipe(ofActionDispatched(TakeChapterPicture), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.screenshotMode = true;
        this.changeDetection.detectChanges();
      });

    this.actions
      .pipe(ofActionCompleted(TakeChapterPictureSuccess), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.screenshotMode = false;
        this.changeDetection.markForCheck();
      });
  }

  bindTagEvents() {
    // Create a HammerJS manager so we can custom define single and double click
    const mc = new Hammer(this.tagRef.nativeElement, { domEvents: true });

    mc.get('pan').set({ direction: Hammer.DIRECTION_ALL, threshold: 3 });

    mc.on('pan', (event) => {
      if (!this.tagDraggable) {
        return;
      }

      event.srcEvent.stopPropagation();

      if (!this.cachedTagOffset) {
        this.cachedTagOffset = this.label.offset;
      }

      const offset: [number, number] = [
        this.cachedTagOffset[0] + event.deltaX,
        this.cachedTagOffset[1] + event.deltaY,
      ];

      this.label.offset = offset;

      if (event.isFinal) {
        this.cachedTagOffset = null;
        this.offsetUpdated.next({ labelId: this.label.id, offset });
      }
    });

    mc.on('tap', (event) => {
      event.srcEvent.preventDefault();
      event.srcEvent.stopPropagation();
      this.tagClicked.next(event);
    });

    this.hammerManagers.push(mc);
  }

  bindPinEvents() {
    // Create a HammerJS manager so we can custom define single and double click
    const mc = new Hammer(this.pinRef.nativeElement);
    mc.get('pan').set({ direction: Hammer.DIRECTION_ALL, threshold: 4 });

    mc.on('pan', (event) => {
      if (!this.pinDraggable) {
        return;
      }

      event.srcEvent.stopPropagation();

      // While pinDragging is true, setCanvasPos won't fire, which allows us to
      // change the pin X and Y translation without interference from the engine.
      this.pinDragging = true;

      let { layerX, layerY } = event.changedPointers[0];

      layerX = layerX - pinRadius;
      layerY = layerY - pinRadius;

      // Marker is updated by the engine every tick, so we have to kill it or else the engine
      // will overwrite our changes.
      if (this.marker) {
        this.deleteMarker(this.marker);
      }

      // Manually update the pin position to the mouse cursor position
      this.renderer.setStyle(
        this.labelContainerRef.nativeElement,
        'transform',
        `translate(${layerX}px, ${layerY}px)`,
      );

      if (event.isFinal) {
        this.pinDragging = false;
        const label = { ...this.label };

        // If the pin is dropped on a node, update the position. Otherwise just revert the pin back to
        // it's original location.
        const hit = Human.renderer.pick.pick(layerX, layerY, { rayPick: true });
        const pinOnObject = typeof hit.objectId === 'string';
        if (pinOnObject) {
          label.dir = this.cameraService.getDirection(hit);
          label.objectId = hit.objectId;
          label.barycentric = [hit.barycentric[0], hit.barycentric[1], hit.barycentric[2]];
          label.pos = [hit.worldPos[0], hit.worldPos[1], hit.worldPos[2]];
          label.primitiveIndex = hit.primitiveIndex;
        }
        const shouldPreserve = pinOnObject;
        this.labelUpdated.next({ label, shouldPreserve });
      }
    });

    mc.on('tap', (event) => {
      event.srcEvent.preventDefault();
      event.srcEvent.stopImmediatePropagation();
      this.pinClicked.next(event);
    });

    this.hammerManagers.push(mc);
  }

  bindHoverEvents() {
    const label = this.labelRef.nativeElement;

    this.renderer.listen(label, 'mouseenter', () => {
      this.hoverStart.next({ labelId: this.label.id });
    });

    this.renderer.listen(label, 'mouseleave', () => {
      this.hoverEnd.next({ labelId: this.label.id });
    });
  }

  initLabel(label: LabelModel) {
    this.initMarker(label);
    this.ensureObjectId(label);
    this.initObjectSubscription(label);
  }

  initMarker(label: LabelModel) {
    if (this.marker) {
      this.deleteMarker(this.marker);
    }

    this.marker = Human.scene.markers.create({
      // track marker by label id
      id: label.id,
      pos: label.pos,
      dir: label.dir,
      objectId: label.objectId,
      primitiveIndex: label.primitiveIndex,
      barycentric: label.barycentric,
      renderableIndex: label.renderableIndex || 0,
    });
  }

  checkLabelDisplay() {
    return {
      id: this.label.id,
      shown: this.label.shown,
      inView: this.isOnCanvas(),
      objectShown: true,
    };
  }

  // labels should always have an objectId
  ensureObjectId(label: LabelModel) {
    if (!label.objectId) {
      const [x, y] = this.marker.canvasPos;
      // attach to object: if hit
      const hit = Human.renderer.pick.pick(x, y, { rayPick: true });
      if (hit.objectId) {
        label.objectId = hit.objectId;
        label.states.forEach((labelState) => {
          labelState.errands.forEach((errand) => {
            if (errand.type === ErrandType.flyToObject) {
              errand.value = hit.objectId;
            }
          });
        });
        this.labelUpdated.next({ label, shouldPreserve: undefined });
      }
    }
  }

  initObjectSubscription(label) {
    if (this.object$) {
      this.object$.unsubscribe();
    }

    // Listen to attached node and check for visibility Don't show labels with
    // hidden nodes
    if (label.objectId) {
      this.object$ = this.store
        .select(EngineState.getViewGraphNode)
        .pipe(
          map((filterFn) => filterFn(label.objectId)),
          filter((node) => !!node),
          takeUntil(this.ngUnsubscribe),
        )
        .subscribe((node) => {
          this.objectShown = node.boolState.shown;
        });
    }
  }

  deleteMarker(marker: MarkerModel) {
    Human.scene.markers.delete(marker.id);
    this.marker = null;
  }

  setCanvasPos() {
    if (this.pinDragging || !this.marker) {
      return;
    }

    this.depth = -this.marker.viewPos[2];

    // The position can update the display state via the shown property
    // Only call if shown has changed
    const isOccluded = this.isOccluded();
    const ignoreOcclusion = this.isOcclusionIgnored();
    const isOnCanvas = this.isOnCanvas();
    const considerOccluded = isOccluded && !ignoreOcclusion;

    // if the label isn't attached to an object Id,
    // `this.objectShown` will always be undefined.

    const shown =
      this.objectShown === undefined
        ? isOnCanvas && !considerOccluded
        : isOnCanvas && this.objectShown && !considerOccluded;

    const hasShownChanged = shown !== this.shown;
    if (hasShownChanged) {
      this.shown = shown;
      this.setDisplay(shown);
    }

    // this.setOpacity(isOccluded); // revisit this
    this.setElementPosition();
    this.setElementScale();
    this.setTagPosition();
    this.setWirePosition();

    // publish position updates
    const { 0: x, 1: y } = this.marker.canvasPos;
    this.positionUpdated$.next({
      id: this.label.id,
      canvasPos: { x, y },
      occluded: considerOccluded,
    });
  }

  isOcclusionIgnored() {
    return this.selected || !this.label.occludable;
  }

  isOccluded(): boolean {
    return this.depth < 0 || this.marker.occluded;
  }

  isOnCanvas(): boolean {
    const { offsetWidth, offsetHeight } = this.labelsService.getLabelContainer().nativeElement;
    const [x, y] = this.marker.canvasPos;
    const onCanvas = x > 0 && y > 0 && x < offsetWidth && y < offsetHeight;
    return onCanvas;
  }

  /**
   * Show and hide labels
   */
  setDisplay(shown: boolean) {
    const labelEl = this.labelRef.nativeElement;
    const display = shown ? 'visible' : 'hidden';
    this.renderer.setStyle(labelEl, 'visibility', display);

    // This is created for the EventsEnum.HotspotsDisplayUpdated event
    this.emitDisplayUpdated({
      id: this.label.id,
      inView: this.isOnCanvas(),
      shown: shown,
      objectShown: this.objectShown,
    });
  }

  setOpacity(isOccluded: boolean) {
    let opacity = fullOpacity;

    if (!isOccluded) {
      this.renderer.setStyle(this.labelContainerRef.nativeElement, 'opacity', 1);
    } else {
      if (this.marker.viewAngle >= fadeStartAngle) {
        const angleDiff = this.marker.viewAngle - fadeStartAngle;
        const scaledOpacity = (angleDiff / fadeRange) * fullOpacity;
        opacity = Math.max(1 - scaledOpacity, 0);
      }

      this.renderer.setStyle(this.labelContainerRef.nativeElement, 'opacity', opacity);
    }
  }

  setElementPosition() {
    const markerPos = this.marker.canvasPos;
    this.renderer.setStyle(
      this.labelContainerRef.nativeElement,
      'transform',
      `translate(${markerPos[0]}px, ${markerPos[1]}px)`,
    );
  }

  setElementScale() {
    let scale: number;

    if (!this.fixedScale) {
      const sceneWidth = this.store.selectSnapshot(EngineState.getEnginePosition).width;
      scale = sceneWidth > 1440 ? sceneWidth / 1440 : 1;
    } else {
      scale = this.fixedScale;
    }

    // Scale label elements and update position accordingly
    const widthGain = 32 * scale - 32; // 32 is the fixed height of the label component
    this.renderer.setStyle(this.labelRef.nativeElement, 'transform-origin', 'top left');
    this.renderer.setStyle(
      this.labelRef.nativeElement,
      'transform',
      `translate(-${widthGain / 2}px, -${widthGain / 2}px) scale(${scale}, ${scale})`,
    );
  }

  setTagPosition() {
    const offset = this.label.offset;
    const tagWidth = this.tagRef.nativeElement.offsetWidth;
    const side = this.label.offset[0] > 0 ? 'left' : 'right';

    switch (side) {
      case 'left':
        this.renderer.setStyle(
          this.tagRef.nativeElement,
          'transform',
          `translate(${16 + offset[0]}px, ${offset[1]}px)`,
        );
        break;

      case 'right':
        this.renderer.setStyle(
          this.tagRef.nativeElement,
          'transform',
          `translate(${16 + offset[0] - tagWidth}px, ${offset[1]}px)`,
        );
        break;
    }
  }

  setWirePosition() {
    // eslint-disable-next-line prefer-const
    let [x, y] = this.label.offset;

    // For annotation and hotspot, move line down to below title
    switch (this.label.theme) {
      case LabelThemeEnum.Annotation:
      case LabelThemeEnum.Classic:
        const height = this.titleRef.nativeElement.offsetHeight - 16 - 2; // 16px is half of pin, 2 is border
        y += height;
        break;
    }

    const wireLength = Math.sqrt(x * x + y * y);

    this.renderer.setStyle(this.wireRef.nativeElement, 'width', `${wireLength}px`);
    this.renderer.setStyle(
      this.wireRef.nativeElement,
      'transform',
      `rotate(${Math.atan2(y, x)}rad)`,
    );
  }

  emitDisplayUpdated(event) {
    // Never emit context-label events
    if (this.label.id === ContextLabelId) {
      return;
    }

    // Don't emit events that lack details
    if (event.inView == null || event.shown == null) {
      return;
    }

    this.displayUpdated$.next(event);
  }
}
